"use client"

import type { FunctionComponent } from "react"

import { isBefore } from "date-fns"
import dynamic from "next/dynamic"
import { useState } from "react"

import { Button } from "@jobteaser/spark/components/Button"
import { HeartOutlined } from "@jobteaser/spark/components/Icons/HeartOutlined"

import { useRegistrationForm } from "@/features/eventDetails/components/Context/RegistrationFormContext"
import { useTrackGenericClickEvent } from "@/modules/tracking/useTrackGenericClickEvent"

import styles from "./MainButtonBlock.module.css"

const DynamicLoginModal = dynamic(
  () => import("@/modules/authentication/components/LoginModal/LoginModal").then(mod => mod.LoginModal),
  { ssr: false }
)

const DynamicNewLoginModal = dynamic(
  () => import("@/modules/authentication/components/LoginModal/NewLoginModal").then(mod => mod.NewLoginModal),
  { ssr: false }
)

type MainButtonBlockUnloggedProps = {
  isWwwCareerCenter: boolean
  careerCenterName: string
  registrationDeadline: string | Date
  saveLabel: string
  startDate: string | Date
  subscribeLabel: string
}

export const MainButtonBlockUnlogged: FunctionComponent<MainButtonBlockUnloggedProps> = ({
  careerCenterName,
  isWwwCareerCenter,
  registrationDeadline,
  startDate: rawStartDate,
  saveLabel,
  subscribeLabel,
}) => {
  const { isNewSignInFlowEnabled } = useRegistrationForm()
  const trackEvent = useTrackGenericClickEvent()
  const [isLoginFlowOpen, setIsLoginFlowOpen] = useState(false)

  const currentDate = new Date()
  const startDate = new Date(rawStartDate || "")
  const hasEventNotStarted = isBefore(currentDate, startDate)
  const isRegistrationDeadlineNotExceeded = isBefore(currentDate, new Date(registrationDeadline))
  const isDisplayedFakeSaveButton = hasEventNotStarted && isRegistrationDeadlineNotExceeded

  const onClick = (eventType: "save" | "subscription"): void => {
    const roleName = eventType === "save" ? "event_ad_save" : "event_ad_subscription_click"
    trackEvent(roleName)
    setIsLoginFlowOpen(true)
  }

  return (
    <>
      {/* Login Flow */}
      {isLoginFlowOpen && isNewSignInFlowEnabled && (
        <DynamicNewLoginModal
          careerCenterName={careerCenterName}
          isWwwCareerCenter={isWwwCareerCenter}
          onClose={() => setIsLoginFlowOpen(false)}
        />
      )}
      {isLoginFlowOpen && !isNewSignInFlowEnabled && (
        <DynamicLoginModal
          careerCenterName={careerCenterName}
          isWwwCareerCenter={isWwwCareerCenter}
          onClose={() => setIsLoginFlowOpen(false)}
        />
      )}

      <div className={styles.buttons}>
        <Button
          data-testid="event-details-subscribe-button-unlogged"
          onClick={() => onClick("subscription")}
          role="button"
          title={subscribeLabel}
          variant="highlight"
        >
          {subscribeLabel}
        </Button>

        {isDisplayedFakeSaveButton && (
          <Button
            className={styles.saveButton}
            data-testid="event-details-save-button-unlogged"
            icon={HeartOutlined}
            onClick={() => onClick("save")}
            role="button"
            title={saveLabel}
            variant="secondary"
          >
            {saveLabel}
          </Button>
        )}
      </div>
    </>
  )
}
